import { useState } from "react";
import { Button, Form, Card, Badge } from "react-bootstrap";

/**
 * Drag-reorder / add / edit / remove extra fields definition for an event.
 * The shape of each field matches the backend IExtraFieldDefinition interface
 * (channel-api/src/@types/event/index.ts).
 *
 * Storage: `schema` is an array of field-definition objects; `onChange` is
 * called with the full new array every time the user edits anything.
 */

const FIELD_TYPES = [
  { value: "text", label: "Texto curto" },
  { value: "textarea", label: "Texto longo" },
  { value: "number", label: "Número" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone" },
  { value: "cpf", label: "CPF" },
  { value: "date", label: "Data" },
  { value: "select", label: "Seleção (uma opção)" },
  { value: "multi_select", label: "Seleção (várias)" },
  { value: "radio", label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
];

const NEEDS_OPTIONS = new Set([
  "select",
  "multi_select",
  "radio",
  "checkbox",
]);

function makeEmptyField() {
  return {
    id: "",
    type: "text",
    label: "",
    placeholder: "",
    required: false,
    max_length: undefined,
    min: undefined,
    max: undefined,
    options: [],
    show_on: "per_attendee",
    help_text: "",
  };
}

function slugifyId(raw) {
  return (raw || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export default function ExtraFieldsEditor({ schema = [], onChange }) {
  const [expandedIdx, setExpandedIdx] = useState(null);

  function update(nextSchema) {
    onChange(nextSchema);
  }

  function handleAdd() {
    const next = [...schema, makeEmptyField()];
    update(next);
    setExpandedIdx(next.length - 1);
  }

  function handleRemove(idx) {
    const ok = window.confirm("Remover este campo?");
    if (!ok) return;
    update(schema.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
  }

  function handleMove(idx, dir) {
    const next = [...schema];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    update(next);
    setExpandedIdx(target);
  }

  function handleField(idx, patch) {
    update(schema.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  function handleOption(idx, optIdx, patch) {
    const field = schema[idx];
    const nextOpts = (field.options || []).map((o, i) =>
      i === optIdx ? { ...o, ...patch } : o
    );
    handleField(idx, { options: nextOpts });
  }

  function handleAddOption(idx) {
    const field = schema[idx];
    const nextOpts = [...(field.options || []), { value: "", label: "" }];
    handleField(idx, { options: nextOpts });
  }

  function handleRemoveOption(idx, optIdx) {
    const field = schema[idx];
    const nextOpts = (field.options || []).filter((_, i) => i !== optIdx);
    handleField(idx, { options: nextOpts });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Campos extras do formulário público
          </span>
          <span className="text-xs text-slate-500">
            Defina aqui os campos adicionais que serão pedidos aos participantes
            (além de nome, e-mail, telefone).
          </span>
        </div>
        <Button size="sm" variant="outline-primary" onClick={handleAdd}>
          + Adicionar campo
        </Button>
      </div>

      {schema.length === 0 ? (
        <div className="text-sm text-slate-500 italic border border-dashed border-slate-300 rounded-md p-6 text-center">
          Nenhum campo extra. Clique em "Adicionar campo" para criar um.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {schema.map((field, idx) => {
            const isOpen = expandedIdx === idx;
            const needsOptions = NEEDS_OPTIONS.has(field.type);
            return (
              <Card key={`fld-${idx}`} className="border border-slate-200">
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-slate-400 text-xs font-mono">
                      #{idx + 1}
                    </span>
                    <span className="font-semibold text-slate-800 truncate">
                      {field.label || (
                        <em className="text-slate-400 font-normal">
                          sem rótulo
                        </em>
                      )}
                    </span>
                    <Badge bg="light" text="dark" className="border">
                      {FIELD_TYPES.find((t) => t.value === field.type)?.label ||
                        field.type}
                    </Badge>
                    <Badge
                      bg={field.show_on === "per_attendee" ? "info" : "warning"}
                      className="text-xs"
                    >
                      {field.show_on === "per_attendee"
                        ? "Por participante"
                        : "Por inscrição"}
                    </Badge>
                    {field.required && (
                      <Badge bg="danger" className="text-xs">
                        obrigatório
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleMove(idx, -1)}
                      disabled={idx === 0}
                      title="Mover para cima"
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleMove(idx, 1)}
                      disabled={idx === schema.length - 1}
                      title="Mover para baixo"
                    >
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant={isOpen ? "secondary" : "outline-primary"}
                      onClick={() => setExpandedIdx(isOpen ? null : idx)}
                    >
                      {isOpen ? "Fechar" : "Editar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleRemove(idx)}
                    >
                      Remover
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        ID (snake_case)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={field.id}
                        placeholder="ex.: camisa_tamanho"
                        onChange={(e) =>
                          handleField(idx, { id: slugifyId(e.target.value) })
                        }
                      />
                      <Form.Text className="text-xs text-slate-400">
                        Usado como chave no banco. Apenas [a-z0-9_].
                      </Form.Text>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        Rótulo (label)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={field.label}
                        placeholder="ex.: Tamanho da camisa"
                        onChange={(e) =>
                          handleField(idx, { label: e.target.value })
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        Tipo
                      </Form.Label>
                      <Form.Select
                        value={field.type}
                        onChange={(e) => {
                          const newType = e.target.value;
                          const patch = { type: newType };
                          if (NEEDS_OPTIONS.has(newType) && !field.options) {
                            patch.options = [];
                          }
                          handleField(idx, patch);
                        }}
                      >
                        {FIELD_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        Escopo
                      </Form.Label>
                      <Form.Select
                        value={field.show_on}
                        onChange={(e) =>
                          handleField(idx, { show_on: e.target.value })
                        }
                      >
                        <option value="per_attendee">
                          Por participante (um por pessoa)
                        </option>
                        <option value="per_registration">
                          Por inscrição (compartilhado no grupo)
                        </option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        Placeholder
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={field.placeholder || ""}
                        onChange={(e) =>
                          handleField(idx, { placeholder: e.target.value })
                        }
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                        Ajuda (help text)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={field.help_text || ""}
                        onChange={(e) =>
                          handleField(idx, { help_text: e.target.value })
                        }
                      />
                    </Form.Group>

                    {(field.type === "text" ||
                      field.type === "textarea" ||
                      field.type === "email" ||
                      field.type === "phone") && (
                      <Form.Group>
                        <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                          Comprimento máx.
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          value={field.max_length ?? ""}
                          onChange={(e) =>
                            handleField(idx, {
                              max_length:
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    )}

                    {field.type === "number" && (
                      <>
                        <Form.Group>
                          <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                            Mínimo
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={field.min ?? ""}
                            onChange={(e) =>
                              handleField(idx, {
                                min:
                                  e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value),
                              })
                            }
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="text-xs font-semibold text-slate-500 uppercase">
                            Máximo
                          </Form.Label>
                          <Form.Control
                            type="number"
                            value={field.max ?? ""}
                            onChange={(e) =>
                              handleField(idx, {
                                max:
                                  e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value),
                              })
                            }
                          />
                        </Form.Group>
                      </>
                    )}

                    <Form.Group className="md:col-span-2 flex items-center gap-2">
                      <Form.Check
                        type="checkbox"
                        id={`required-${idx}`}
                        label="Obrigatório"
                        checked={!!field.required}
                        onChange={(e) =>
                          handleField(idx, { required: e.target.checked })
                        }
                      />
                    </Form.Group>

                    {needsOptions && (
                      <div className="md:col-span-2 flex flex-col gap-2 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Opções
                          </span>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleAddOption(idx)}
                          >
                            + Opção
                          </Button>
                        </div>
                        {(field.options || []).length === 0 ? (
                          <span className="text-xs italic text-slate-400">
                            Nenhuma opção. Adicione pelo menos uma.
                          </span>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {(field.options || []).map((opt, optIdx) => (
                              <div
                                key={`opt-${idx}-${optIdx}`}
                                className="flex items-center gap-2"
                              >
                                <Form.Control
                                  size="sm"
                                  placeholder="valor (ex: p)"
                                  value={opt.value}
                                  onChange={(e) =>
                                    handleOption(idx, optIdx, {
                                      value: e.target.value,
                                    })
                                  }
                                  style={{ maxWidth: 180 }}
                                />
                                <Form.Control
                                  size="sm"
                                  placeholder="rótulo (ex: P - Pequeno)"
                                  value={opt.label}
                                  onChange={(e) =>
                                    handleOption(idx, optIdx, {
                                      label: e.target.value,
                                    })
                                  }
                                />
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    handleRemoveOption(idx, optIdx)
                                  }
                                >
                                  ✕
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
